import React, { useEffect, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';

import { getElProgress } from 'components/Scroller/utils';
import { withScrollerContext } from 'components/Scroller/context';
import { ScrollerStateProps } from 'components/Scroller/types';

function ScrollerModule(props) {
  const {
    id,
    children,
    onResize,
    onUpdate = () => {},
    resizeOnReady = true,
    scroller,
  } = props;

  const {
    ready,
    scrollingElement,
    registerModule,
    unregisterModule,
    moduleClassName,
  } = scroller;

  const [position, setPosition] = useState({ top: 0, height: 0 });
  const ref = useRef();

  const onResizeWithProgress = useCallback(
    event => {
      if (!ref.current) return;
      const { top, right, bottom, left, width, height, x, y } =
        ref.current.getBoundingClientRect();
      const elTop = top + (scrollingElement?.scrollTop || 0);
      setPosition({
        right,
        bottom,
        left,
        width,
        height,
        x,
        y,
        top: elTop,
      });
      const scrollTop = scrollingElement?.scrollTop || 0;
      const progress = getElProgress({
        scrollTop,
        elTop,
        elHeight: height,
        windowHeight: window.innerHeight,
      });

      if (onResize) {
        onResize({ event, progress });
      }

      onUpdate({ progress });
    },
    [setPosition, scrollingElement, onUpdate, onResize],
  );

  const onUpdateWithProgress = useCallback(() => {
    const scrollTop = scrollingElement?.scrollTop || 0;
    const progress = getElProgress({
      scrollTop,
      elTop: position.top,
      elHeight: position.height,
      windowHeight: window.innerHeight,
    });
    onUpdate({ progress, scrollTop });
  }, [position, onUpdate, scrollingElement?.scrollTop]);

  /**
   * Mount/Unmount
   */
  useEffect(() => {
    const { top, right, bottom, left, width, height, x, y } =
      ref.current.getBoundingClientRect();
    setPosition({
      right,
      bottom,
      left,
      width,
      height,
      x,
      y,
      top: top + (scrollingElement?.scrollTop || 0),
    });

    return () => {
      if (unregisterModule) {
        unregisterModule(id);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Register module with Scroller
   * and keep module data up-to-date
   */
  useEffect(() => {
    if (!position.height) return;

    /**
     * The Scroller requires each module to have at least:
     * @param {string} id Unique, get from cms
     * @param {object} ref React ref { current: node }
     * @param {object} position Easiest to snag from el.getBoundingClientRect. Needs at least: { top, height }
     * @param {function} onUpdate Fires in the Scroller raf. Is what does the repainting/heavy lifting for module animations
     * @param {function} onResize Fires in the Scroller
     */
    // eslint-disable-next-line @next/next/no-assign-module-variable
    const module = {
      id,
      ref,
      position,
      onUpdate: onUpdateWithProgress,
      onResize: onResizeWithProgress,
    };
    registerModule(id, module);
  }, [position, id, registerModule, scrollingElement]);

  /**
   * Call initial onResizeWithProgress
   * when all the modules are ready
   */
  useEffect(() => {
    if (resizeOnReady && ready) {
      onResizeWithProgress();
    }
  }, [ready, resizeOnReady]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    /**
     * className needed for scroller to work
     * This is how the Scroller knows when every module has been registered
     */
    <div key={id} ref={ref} className={moduleClassName}>
      {children}
    </div>
  );
}

ScrollerModule.propTypes = {
  id: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  onUpdate: PropTypes.func,
  scrollerId: PropTypes.string,
  onResize: PropTypes.func,
  resizeOnReady: PropTypes.bool,
  scroller: ScrollerStateProps,
  resetReposition: PropTypes.func,
};

export default withScrollerContext(ScrollerModule);
